import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { APPLY_URL } from "@/lib/site-data";

// ---------------------------------------------------------------------------
// Reveal — fade + translate up when scrolled into view
// ---------------------------------------------------------------------------
export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true, as = "div", ...rest }) => {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={`relative ${className}`}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

// ---------------------------------------------------------------------------
// Kicker / red overline label (poa style)
// ---------------------------------------------------------------------------
export const Kicker = ({ children, className = "", dot = false }) => (
  <span
    className={`inline-flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-[0.28em] text-[#2D5BFF] ${className}`}
  >
    {dot && <span className="h-1.5 w-1.5 rounded-full bg-[#2D5BFF]" />}
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// Badge — warning pill used above the hero headline
// ---------------------------------------------------------------------------
export const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full border border-[#2D5BFF]/40 bg-[#2D5BFF]/10 px-4 py-1.5 font-heading text-[11px] font-bold uppercase tracking-[0.18em] text-[#2D5BFF] sm:text-xs ${className}`}
  >
    <AlertTriangle className="h-3.5 w-3.5" />
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// CTAButton — every CTA redirects to APPLY_URL (red, rounded-rect, glow)
// ---------------------------------------------------------------------------
export const CTAButton = ({
  children = "Book Your Free Strategy Call",
  variant = "primary",
  size = "md",
  className = "",
  showArrow = true,
  testId,
}) => {
  const base =
    "group relative inline-flex items-center justify-center gap-2.5 font-heading font-bold uppercase tracking-wide rounded-xl transition-all duration-300 will-change-transform";
  const sizes = {
    md: "px-6 py-3.5 text-sm",
    lg: "px-8 py-4 text-base",
    xl: "px-10 py-5 text-lg sm:text-xl",
  };
  const variants = {
    primary:
      "bg-[#2D5BFF] text-white shadow-[0_10px_40px_-8px_rgba(45,91,255,0.6)] hover:shadow-[0_14px_50px_-6px_rgba(45,91,255,0.85)] hover:-translate-y-0.5",
    outline:
      "border border-[#D6DBE7] bg-white text-[#0F1117] shadow-sm hover:border-[#2D5BFF] hover:bg-[#EEF2FF] hover:text-[#2D5BFF] hover:-translate-y-0.5",
  };
  return (
    <a
      href={APPLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {children}
      {showArrow && (
        <ArrowRight className="h-[1.1em] w-[1.1em] transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </a>
  );
};

// ---------------------------------------------------------------------------
// CountUp — animated number when it enters view
// ---------------------------------------------------------------------------
export const CountUp = ({ value, prefix = "", suffix = "", decimals = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display.toFixed(decimals)}
      {suffix}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Marquee — continuous editorial ribbon
// ---------------------------------------------------------------------------
export const Marquee = ({ items, duration = 45, className = "", itemClassName = "" }) => (
  <div className={`marquee-pause relative flex w-full overflow-hidden ${className}`}>
    <div
      className="animate-marquee flex shrink-0 items-center"
      style={{ "--marquee-duration": `${duration}s` }}
    >
      {items.concat(items).map((item, i) => (
        <span key={i} className={itemClassName}>
          {item}
          <span className="mx-8 text-[#2D5BFF]/50 sm:mx-12">/</span>
        </span>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Section wrapper with consistent container spacing
// ---------------------------------------------------------------------------
export const Section = ({ id, children, className = "", ...rest }) => (
  <section id={id} data-testid={rest["data-testid"] || id} className={`relative w-full overflow-hidden ${className}`}>
    <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">{children}</div>
  </section>
);
